import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/lib/api";
import type {
  CreateWeeklyReportInput,
  ReviewWeeklyReportInput,
  WeeklyComplianceResponse,
  WeeklyReport,
  WeeklyReportsListResponse,
} from "@/types";

/**
 * Weekly status report hooks for the project detail page.
 *
 * Backend (packages/weekly-reports, specs/api/weekly-reports.yaml):
 *   - GET  /api/projects/{projectId}/weekly-reports → { reports }  (pm/sa/leadership/admin;
 *          pm sees only their own; others see every submitted report; drafts only to their author)
 *   - POST /api/projects/{projectId}/weekly-reports → WeeklyReport (create — draft or submitted)
 *   - PUT  /api/projects/{projectId}/weekly-reports/{reportId} → WeeklyReport
 *          (continue a draft, submit it, or edit + re-submit a submitted report)
 *   - POST /api/projects/{projectId}/weekly-reports/{reportId} → WeeklyReport
 *          (T-28 PMO review — leadership/admin approve or request changes)
 *   - GET  /api/reporting/weekly-compliance?week=YYYY-MM-DD → WeeklyComplianceResponse (T-21)
 */

/** True for a V047 draft. Rows without a `status` (pre-V047 API) are submitted reports. */
export function isDraftReport(report: Pick<WeeklyReport, "status">): boolean {
  return report.status === "draft";
}

/**
 * Submitted reports only, preserving the API's week_ending DESC order. Every "latest report"
 * surface (Overview snapshot, LatestWeeklyReportCard) and the prior-phase carry-forward must read
 * from this — a draft is unpublished and must never stand in for the current status.
 */
export function submittedReportsOnly(
  reports: readonly WeeklyReport[] | undefined,
): WeeklyReport[] {
  return (reports ?? []).filter((r) => !isDraftReport(r));
}

/**
 * T-27: client-side filter for the Weekly Reports list. Review filters match SUBMITTED reports by
 * `review_status`; `draft` matches the viewer's own drafts. A pre-V052 submitted row with no
 * `review_status` only appears under `all`.
 */
export type WeeklyReportListFilter =
  | "all"
  | "pending"
  | "approved"
  | "changes_requested"
  | "draft";

export function matchesReportListFilter(
  report: Pick<WeeklyReport, "status" | "review_status">,
  filter: WeeklyReportListFilter,
): boolean {
  if (filter === "all") return true;
  if (filter === "draft") return isDraftReport(report);
  return !isDraftReport(report) && report.review_status === filter;
}

/** Fetch the weekly reports for a project (server applies the per-role visibility filter). */
export function useWeeklyReports(projectId: string) {
  const client = useApiClient();

  return useQuery<WeeklyReport[]>({
    queryKey: ["weekly-reports", projectId],
    queryFn: async () => {
      const response = await client.get<WeeklyReportsListResponse>(
        `/api/projects/${projectId}/weekly-reports`,
      );
      return response.data.reports ?? [];
    },
    enabled: !!projectId,
  });
}

/** Submit (or upsert for the same week) a weekly report. Invalidates the project's report list. */
export function useSubmitWeeklyReport(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<WeeklyReport, unknown, CreateWeeklyReportInput>({
    mutationFn: async (input) => {
      const response = await client.post<WeeklyReport>(
        `/api/projects/${projectId}/weekly-reports`,
        input,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["weekly-reports", projectId],
      });
    },
  });
}

/**
 * Edit an existing report — continue a draft (`status: 'draft'`), submit a draft, or edit and
 * re-submit a submitted report (`status: 'submitted'`). Invalidates the project's report list.
 */
export function useUpdateWeeklyReport(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<
    WeeklyReport,
    unknown,
    { reportId: string; input: CreateWeeklyReportInput }
  >({
    mutationFn: async ({ reportId, input }) => {
      const response = await client.put<WeeklyReport>(
        `/api/projects/${projectId}/weekly-reports/${reportId}`,
        input,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["weekly-reports", projectId],
      });
    },
  });
}

/**
 * T-28: PMO review of a submitted report (leadership/admin). Invalidates the project's report list
 * and every cached compliance week so the dashboard reflects the decision.
 */
export function useReviewWeeklyReport(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<
    WeeklyReport,
    unknown,
    { reportId: string; input: ReviewWeeklyReportInput }
  >({
    mutationFn: async ({ reportId, input }) => {
      const response = await client.post<WeeklyReport>(
        `/api/projects/${projectId}/weekly-reports/${reportId}`,
        input,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["weekly-reports", projectId],
      });
      void queryClient.invalidateQueries({ queryKey: ["weekly-compliance"] });
    },
  });
}

/**
 * T-21: weekly-report compliance for the week containing `week` (YYYY-MM-DD). Omit `week` for the
 * current America/Chicago report week.
 */
export function useWeeklyCompliance(week?: string, enabled = true) {
  const client = useApiClient();

  return useQuery<WeeklyComplianceResponse>({
    queryKey: ["weekly-compliance", week ?? "current"],
    queryFn: async () => {
      const response = await client.get<WeeklyComplianceResponse>(
        "/api/reporting/weekly-compliance",
        week ? { params: { week } } : undefined,
      );
      return response.data;
    },
    enabled,
  });
}
